// Percorso file: src/pages/home/HomeContext.jsx
//
// FINESTRA DI SCELTA DEL RUOLO (rotta /home/context).
// Ricostruita sul modello nuovo: elenca i ruoli POSSEDUTI secondo il
// gate unico (`getActiveRoles`, che somma il `role` di primo livello e i
// context ACTIVE) e naviga con l'unica mappa ROLE_ROUTES. La vecchia
// mappa case-sensitive interna alla pagina — la quarta dell'app,
// audit 08 §1.6 — non esiste più.
//
// Ci arriva chi possiede due o più ruoli: l'instradatore manda qui ogni
// volta, senza memoria dell'ultima veste (diario, decisione B).
//
// Grafica: gruppo-scelta (doc 07 §6) con un pulsante scuro (§5.2) per
// ruolo. Nella banda non compare «Switch role»: questa pagina È lo
// switch.
//
// F6 (cantiere CONTEXT, modello B) — LO SWITCH IMPOSTA IL CAPPELLO.
// Fino a ieri questa pagina navigava soltanto: il cappello (activeContext)
// lo impostava solo AuthContext all'ingresso, col PRIMO context ACTIVE
// (`setActiveContext(contexts[0] || null)`), e le viste che leggono il
// cappello — le cinque viste personali del calendario di P1, Attendance,
// Messaging — rispondevano a quel context invece che al ruolo scelto qui.
// Da F6 il clic IMPOSTA il cappello sulla tessera del ruolo scelto (ruolo
// E squadre) e poi naviga. Il resto non cambia: l'ingresso continua a
// mettere `contexts[0]`, e il cappello vale per la sessione — un ricarico
// torna a `contexts[0]` come oggi. La scelta PERSISTENTE (squadra,
// stagione) non è di questa pagina: è del cantiere Season.
//
// F7b — IL CAPPELLO SI IMPOSTA ANCHE SENZA TESSERA. F6 lo impostava solo se
// trovava una tessera del ruolo scelto; per ADMIN e SUPERADMIN, che una
// tessera non ce l'hanno (stanno nel campo `role`), restava addosso il
// cappello precedente. Ora in quel caso si indossa un cappello SINTETICO —
// ruolo scelto, nessuna squadra — così il ruolo indossato è sempre quello su
// cui si è appena cliccato. Vedi la nota su `chooseRole`.

import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import HomeShell from "../../components/HomeShell";
import { UnknownRoleView } from "../../components/SystemView";
import {
  getActiveRoles,
  roleLabel,
  ROLE_ROUTES,
  canonicalRole,
  normalizeRole,
} from "../../auth/access";

const HomeContext = () => {
  const navigate = useNavigate();
  const { user, setActiveContext } = useAuth();

  const roles = getActiveRoles(user);
  const withHome = roles.filter((role) => ROLE_ROUTES[role]);
  const withoutHome = roles.filter((role) => !ROLE_ROUTES[role]);

  // Nessuno dei ruoli posseduti ha una home: stessa schermata
  // dell'instradatore, mai un rimbalzo muto.
  if (withHome.length === 0) return <UnknownRoleView roles={roles} />;

  // La scelta: prima il cappello, poi la rotta.
  //
  // Questa pagina elenca RUOLI (stringhe canoniche di `getActiveRoles`),
  // non tessere: dal ruolo scelto si risale alla tessera cercando in
  // `user.contexts` la PRIMA ATTIVA il cui ruolo CANONICO coincide. Il
  // confronto passa da `canonicalRole` su ENTRAMBI i lati — mai una
  // stringa grezza — se no una tessera storica `role: "Manager"` non
  // risponderebbe al pulsante «Coach» che essa stessa ha fatto comparire.
  // Lo status si legge con `normalizeRole`, non con `canonicalRole`: è uno
  // status, non un ruolo, e il vocabolario lì sarebbe un danno (stesso
  // metro di access.js e del backend, server.js).
  //
  // «La PRIMA attiva» è la stessa regola dell'ingresso: chi ha due tessere
  // dello stesso ruolo canonico (due squadre da Coach) indossa quella in
  // testa alla lista. Scegliere QUALE squadra non è di questa pagina —
  // qui si sceglie il RUOLO — ed è il cantiere Season a darne il selettore.
  //
  // TESSERA NON TROVATA → CAPPELLO SINTETICO (F7b). Capita per il ruolo di
  // primo livello, che una tessera non ce l'ha: ADMIN e SUPERADMIN stanno nel
  // campo `role` e non fra i contexts (dalla porta Manage Contexts non si
  // scrivono nemmeno — il backend ammette lì solo PLAYER / COACH / PARENT), e
  // i ruoli elencati qui vengono dalla SOMMA `role` + contexts ACTIVE.
  //
  // Fino a F6 in quel caso si navigava lasciando il cappello com'era, ed era
  // il difetto: un coach che passa ad «Admin» arrivava sulla home d'admin
  // ancora vestito da COACH, e le viste che leggono il cappello — le cinque
  // personali del calendario, Attendance, Messaging — continuavano a
  // rispondergli da coach, con le SUE squadre. Il cappello vecchio è peggio di
  // nessun cappello: mente sul ruolo indossato.
  //
  // Da F7b il cappello si imposta SEMPRE: o la tessera trovata, o uno
  // sintetico — il ruolo scelto in grafia canonica, e NESSUNA squadra. È
  // fedele a com'è fatto quel ruolo: un ADMIN non ha squadre proprie, vede il
  // club intero perché è admin, non perché una tessera glielo assegni.
  //
  // I campi sono i minimi che i lettori del cappello si aspettano, nelle DUE
  // forme, perché entrambe sono ancora lette: `role` e `status` (che
  // `currentRole` esige ACTIVE, se no il cappello non vale), la coppia nuova
  // `teams`/`allTeams` e la vecchia `team`/`approvedTeams`. Vuote tutte e
  // due: una sola vuota lascerebbe l'altra a parlare al posto sua.
  //
  // ⚠️ NON è una tessera e non si salva: vive nello stato di AuthContext per
  // la sessione, come ogni cappello di F6. Nessuna persistenza, e nulla che
  // arrivi mai a `user.contexts` o a Firestore.
  const chooseRole = (role) => {
    const wanted = canonicalRole(role);

    const tessera = Array.isArray(user?.contexts)
      ? user.contexts.find(
          (ctx) =>
            ctx &&
            normalizeRole(ctx.status) === "ACTIVE" &&
            canonicalRole(ctx.role) === wanted
        )
      : undefined;

    setActiveContext(
      tessera || {
        role: wanted,
        status: "ACTIVE",
        teams: [],
        allTeams: false,
        team: "",
        approvedTeams: [],
      }
    );

    navigate(ROLE_ROUTES[role]);
  };

  return (
    <HomeShell showSwitch={false}>
      <h1 className="qpsc-page-title">Choose role</h1>

      <div className="qpsc-choice-group">
        <span className="qpsc-choice-group__label">Continue as</span>
        {withHome.map((role) => (
          <button
            key={role}
            type="button"
            className="qpsc-btn-dark"
            onClick={() => chooseRole(role)}
          >
            {roleLabel(role)}
          </button>
        ))}
      </div>

      {withoutHome.length > 0 && (
        <p className="qpsc-note">
          Not available yet: {withoutHome.join(", ")}.
        </p>
      )}
    </HomeShell>
  );
};

export default HomeContext;
